import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { alpha } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NoteIcon from '@mui/icons-material/Note';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupIcon from '@mui/icons-material/Group';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../services/firebase';
import { getContrastText } from '../utils/colorUtils';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EventIcon from '@mui/icons-material/Event';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlagIcon from '@mui/icons-material/Flag';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const baseMenuItems = [
 
  {
    id: 'calendar',
    title: 'Calendário',
    icon: CalendarMonthIcon,
    color: '#2196F3'
  },
  {
    id: 'habits',
    title: 'Hábitos',
    icon: CheckCircleIcon,
    color: '#4CAF50'
  },
  {
    id: 'goals',
    title: 'Metas',
    icon: FlagIcon,
    color: '#FF9800'
  },
  {
    id: 'challenges',
    title: 'Desafios',
    icon: EmojiEventsIcon,
    color: '#9C27B0'
  },
  {
    id: 'pomodoro',
    title: 'Pomodoro',
    icon: TimerIcon,
    color: '#E91E63'
  },
  {
    id: 'player',
    title: 'Player',
    icon: PlayArrowIcon,
    color: '#2196F3'
  },
  {
    id: 'notes',
    title: 'Notas',
    icon: NoteIcon,
    color: '#795548'
  },
  {
    id: 'events',
    title: 'Eventos',
    icon: EventIcon,
    color: '#FF9800'
  },
  {
    id: 'statistics',
    title: 'Estatísticas',
    icon: BarChartIcon,
    color: '#607D8B'
  },
  // Temporariamente desativado até finalizar ajustes
  // {
  //   id: 'meditations',
  //   title: 'Meditações',
  //   icon: SelfImprovementIcon,
  //   color: '#00BCD4'
  // },
  {
    id: 'bookRecommendations',
    title: 'Dicas de Livros',
    icon: MenuBookIcon,
    color: '#3F51B5'
  },
  {
    id: 'settings',
    title: 'Configurações',
    icon: SettingsIcon,
    color: '#9E9E9E'
  },
  {
    id: 'help',
    title: 'Ajuda',
    icon: HelpIcon,
    color: '#00BCD4'
  }
];

const adminMenuItem = {
  id: 'userManagement',
  title: 'Usuários',
  icon: GroupIcon,
  color: '#FF5722',
  adminOnly: true
};

function Menu({ customColors, onNavigate: handleNavigate }) {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const textColor = customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText;

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser?.uid) {
        const adminStatus = await isUserAdmin(currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  const menuItems = [
    ...baseMenuItems,
    ...(isAdmin ? [adminMenuItem] : [])
  ];

  const onNavigate = (id) => {
    handleNavigate(id);
  };

  return (
    <PageLayout
      title="Início"
      hideAddButton
      customColors={customColors}
    >
      <Grid 
        container 
        spacing={3} 
        sx={{ mt: isMobile ? -10 : 0 }}
      >
        {menuItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          const IconComponent = item.icon;
          
          return (
            <Grid item xs={4} sm={4} md={3} mt={0} key={item.id}>
              <Card 
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDarkMode ? '0 4px 20px 0 rgba(0,0,0,0.5)' : 4,
                    bgcolor: isDarkMode 
                      ? alpha(theme.palette.background.paper, 0.8) 
                      : alpha(theme.palette.background.paper, 0.9)
                  }
                }}
            
              >
                <CardActionArea
                  onClick={() => onNavigate(item.id)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: { xs: 2.5, sm: 3 },
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <IconComponent 
                    sx={{ 
                      fontSize: 48,
                      color: primaryColor,
                      mb: 2,
                      transition: 'color 0.3s ease-in-out',
                      opacity: isDarkMode ? 0.9 : 1
                    }}
                  />
                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '1.2rem' },
                      fontWeight: 'bold',
                      color: isDarkMode ? 'white' : 'black',
                      transition: 'color 0.3s ease-in-out',
                      opacity: isDarkMode ? 0.9 : 1
                    }}
                  >
                    {item.title}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </PageLayout>
  );
}

export default Menu; 